import React, { useState } from "react";
import * as Database from "utils/database.utils";
import { useSettings } from "context/Settings.context";
import { createLocalDownload } from "utils/export.utils";
import Button from "components/Common/Button/Button";
import Modal from "components/Common/Modal/Modal";
import { MorningMeetingFile } from "./MorningMeetingDetails.types";
import DownloadMeetingFilesModalContainer from "./DownloadMeetingFilesModal.styles";

interface Props {
    onClose: () => void;
    files: MorningMeetingFile[];
    meetingId: string;
}

const FILE_TYPE_LABELS: Record<string, string> = {
    TRANSCRIPT: "Transcript",
    MEETING_NOTES: "Meeting notes",
    RECORDING: "Recording",
};

const DownloadMeetingFilesModal = ({ onClose, files, meetingId }: Props) => {
    const { setToast } = useSettings();
    const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);

    const handleDownload = async () => {
        if (!selectedFileId) {
            setToast({
                show: true,
                type: "error",
                message: "Please select a file to download",
            });
            return;
        }
        setDownloading(true);
        const selectedFile = files.find((f) => f.id === selectedFileId);
        const fileName = selectedFile?.name ?? selectedFileId;
        const url = `api/bqmq_voting/morning-meetings/${meetingId}/files/${selectedFileId}?filename=${encodeURIComponent(
            fileName
        )}`;

        try {
            const response = await Database.get(url);
            if (!response.ok) {
                setToast({
                    show: true,
                    type: "error",
                    message: "Failed to download file",
                });
                return;
            }

            const disposition = response.headers?.get("Content-Disposition");
            const filenameFromHeader = disposition
                ?.split("filename=")[1]
                ?.trim()
                ?.replace(/^"|"$/g, "");
            const downloadFilename = filenameFromHeader || fileName;

            const file = await response.blob();
            createLocalDownload(downloadFilename, file);

            setToast({
                show: true,
                type: "success",
                message: "Download started",
            });
        } catch {
            setToast({
                show: true,
                type: "error",
                message: "Failed to download file",
            });
        } finally {
            setDownloading(false);
        }
    };

    return (
        <DownloadMeetingFilesModalContainer>
            <Modal onClose={onClose}>
                <div className="main">
                    <div className="text">
                        <h3>Download meeting file</h3>
                        <p>Select a file and click Download to open it.</p>
                    </div>
                    {files.length === 0 ? (
                        <p>No files available to download.</p>
                    ) : (
                        <ul className="file-list">
                            {files.map((file) => (
                                <li
                                    key={file.id}
                                    className={`file-item ${
                                        selectedFileId === file.id
                                            ? "selected"
                                            : ""
                                    }`}
                                    onClick={() => setSelectedFileId(file.id)}
                                    onKeyDown={(e) => {
                                        if (
                                            e.key === "Enter" ||
                                            e.key === " "
                                        ) {
                                            e.preventDefault();
                                            setSelectedFileId(file.id);
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                >
                                    <span className="file-name">
                                        {file.name}
                                    </span>
                                    <span className="file-type">
                                        {FILE_TYPE_LABELS[file.type] ??
                                            file.type}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <div className="actions">
                    <Button text="CANCEL" color="flat" click={onClose} />
                    <Button
                        text={downloading ? "DOWNLOADING..." : "DOWNLOAD"}
                        click={handleDownload}
                        disabled={
                            downloading || files.length === 0 || !selectedFileId
                        }
                    />
                </div>
            </Modal>
        </DownloadMeetingFilesModalContainer>
    );
};

export default DownloadMeetingFilesModal;
